import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

type ThreadMessage = {
  id: string;
  authorRole: "agent" | "admin" | "reporter";
  body: string;
  createdAt: string | null;
};

type ThreadResponse = {
  feedback: { id: string; status: string; message: string };
  viewerRole: "admin" | "reporter";
  messages: ThreadMessage[];
};

// Inline triage Q&A thread, used both on the standalone /feedback/:id page
// and embedded in the Messages dashboard as a "Stoop" conversation.
export function FeedbackChat({
  feedbackId,
  showReportContext = false,
}: {
  feedbackId: string;
  showReportContext?: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const { data, isLoading, error } = useQuery<ThreadResponse>({
    queryKey: ["/api/feedback", feedbackId, "thread"],
    enabled: !!feedbackId,
    queryFn: async () => {
      const res = await fetch(`/api/feedback/${feedbackId}/thread`, { credentials: "include" });
      if (!res.ok) throw new Error(res.status === 403 ? "This isn't your report." : "Couldn't load this conversation.");
      return res.json();
    },
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });

  const reply = useMutation({
    mutationFn: async (body: string) => {
      const res = await fetch(`/api/feedback/${feedbackId}/thread`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => "Couldn't send"));
      return res.json();
    },
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["/api/feedback", feedbackId, "thread"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
      toast({ title: "Sent", description: "Thanks — we'll take another look right away." });
    },
    onError: (e: any) => {
      toast({ title: "Couldn't send", description: e?.message || "Try again", variant: "destructive" });
    },
  });

  if (isLoading) return <p className="py-4 text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {(error as any)?.message || "Couldn't load this conversation."}
      </p>
    );
  }

  const messages = data.messages;

  return (
    <div className="space-y-3 pt-3">
      {showReportContext && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            What you reported
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{data.feedback.message}</p>
        </div>
      )}

      <div className="space-y-2">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">No messages yet.</p>
        ) : (
          messages.map((m) => {
            const mine = m.authorRole === "reporter";
            return (
              <div
                key={m.id}
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                  mine
                    ? "ml-auto bg-primary text-primary-foreground"
                    : "mr-auto border border-border bg-card text-foreground"
                }`}
              >
                <div
                  className={`mb-0.5 text-[10px] font-medium uppercase tracking-wide ${
                    mine ? "text-primary-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {m.authorRole === "agent" ? "Stoop" : m.authorRole === "admin" ? "Stoop team" : "You"}
                  {m.createdAt ? ` · ${new Date(m.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}` : ""}
                </div>
                <p className="whitespace-pre-wrap">{m.body}</p>
              </div>
            );
          })
        )}
      </div>

      <div className="space-y-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your answer…"
          className="min-h-[64px] text-sm"
          maxLength={8000}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={reply.isPending}
            onClick={() => {
              const body = draft.trim();
              if (!body) {
                toast({ title: "Write a reply first" });
                return;
              }
              reply.mutate(body);
            }}
          >
            {reply.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
